import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SessionService } from './session.service';
import { OfflineStorageService } from '../shared/offline-storage.service';
import { PassportDetailsStore } from '../shared/passport-details.store';
import { PassportService } from '../shared/passport.service';

const SESSION_EXPIRED_LOGOUT_PREFIX = 'Session expired';
const SERVICE_WORKER_CACHE_CLEAR_DELAY_MS = 5_000;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly session = inject(SessionService);
  private readonly offlineStorage = inject(OfflineStorageService);
  private readonly passportDetails = inject(PassportDetailsStore);
  private readonly passportService = inject(PassportService);

  /**
   * Logout the user and clear all application data
   * This includes localStorage, IndexedDB, and all in-memory stores
   */
  async logout(reason?: string): Promise<void> {
    console.log('🔒 Logging out user...', reason ? `Reason: ${reason}` : '');

    try {
      // 1. Clear localStorage/session and in-memory stores immediately
      this.session.clear();
      this.clearInMemoryStores();

      // 2. Navigate to login page first for responsive UX
      await this.router.navigate(['/login'], {
        queryParams: reason ? { message: reason } : undefined
      });

      // 3. Clear heavy caches in the background (non-blocking, sequential)
      void this.runBackgroundLogoutCleanup(reason).catch((error: unknown) => {
        console.warn('❌ Background logout cleanup failed:', error);
      });

      console.log('✅ Logout navigation done; background cleanup started');
    } catch (error) {
      console.warn('❌ Error during logout:', error);
      // Even if clearing fails, navigate to login
      await this.router.navigate(['/login']);
    }
  }

  private async runBackgroundLogoutCleanup(reason?: string): Promise<void> {
    await this.clearIndexedDB();

    if (this.isSessionExpiryLogout(reason)) {
      return;
    }

    await this.deferClearServiceWorkerCaches();
  }

  private isSessionExpiryLogout(reason?: string): boolean {
    return reason?.startsWith(SESSION_EXPIRED_LOGOUT_PREFIX) ?? false;
  }

  private async deferClearServiceWorkerCaches(): Promise<void> {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, SERVICE_WORKER_CACHE_CLEAR_DELAY_MS);
    });
    await this.clearServiceWorkerCaches();
  }

  /**
   * Clear all IndexedDB data (delete whole DB — reliable on iOS Safari)
   */
  private async clearIndexedDB(): Promise<void> {
    await this.waitUntilPageVisible();
    try {
      await this.offlineStorage.deleteDatabase();
      console.log('✅ IndexedDB cleared');
    } catch (error) {
      console.warn('❌ Failed to clear IndexedDB:', error);
    }
  }

  private waitUntilPageVisible(timeoutMs = 3000): Promise<void> {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const done = (): void => {
        document.removeEventListener('visibilitychange', onChange);
        resolve();
      };
      const onChange = (): void => {
        if (document.visibilityState === 'visible') {
          done();
        }
      };
      document.addEventListener('visibilitychange', onChange);
      setTimeout(done, timeoutMs);
    });
  }

  /**
   * Clear all in-memory stores
   */
  private clearInMemoryStores(): void {
    try {
      // Clear passport details store
      this.passportDetails.clear();
      
      // Clear passport service records
      this.passportService.records.set([]);
      
      console.log('✅ In-memory stores cleared');
    } catch (error) {
      console.warn('❌ Failed to clear in-memory stores:', error);
    }
  }

  /**
   * Clear service worker caches
   */
  private async clearServiceWorkerCaches(): Promise<void> {
    if ('caches' in globalThis) {
      try {
        const cacheStorage = globalThis.caches;
        const cacheNames = await cacheStorage.keys();
        await Promise.all(
          cacheNames.map((cacheName) => cacheStorage.delete(cacheName))
        );
        console.log('✅ Service worker caches cleared');
      } catch (error) {
        console.warn('❌ Failed to clear service worker caches:', error);
      }
    }
  }

  /**
   * Check if user is authenticated (has valid token)
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('accessToken');
    const userId = localStorage.getItem('userID');
    return !!(token && userId);
  }

  /**
   * Get token expiration time from JWT
   * Returns null if token is invalid or doesn't have exp claim
   */
  getTokenExpiration(): Date | null {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return null;

      // Decode JWT (without verification - just reading the payload)
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = JSON.parse(atob(parts[1]));
      
      if (payload.exp) {
        // exp is in seconds, convert to milliseconds
        return new Date(payload.exp * 1000);
      }
      
      return null;
    } catch (error) {
      console.warn('Failed to parse token expiration:', error);
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    const expiration = this.getTokenExpiration();
    if (!expiration) return true; // If we can't determine expiration, assume expired
    
    return new Date() >= expiration;
  }

  /**
   * Start monitoring token expiration
   * This will automatically logout when token expires
   */
  startTokenExpirationMonitoring(): void {
    // Check every minute
    setInterval(() => {
      if (this.isAuthenticated() && this.isTokenExpired()) {
        console.warn('🔒 Token has expired - logging out user');
        void this.logout('Session expired. Please login again.');
      }
    }, 60000); // Check every 60 seconds

    // Also check immediately
    if (this.isAuthenticated() && this.isTokenExpired()) {
      console.warn('🔒 Token has expired - logging out user');
      void this.logout('Session expired. Please login again.');
    }
  }
}

